import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MessageSquare, Send, Search, User, Clock, ArrowLeft, Plus, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import io from 'socket.io-client';

const Messages = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [socket, setSocket] = useState(null);
  const [typing, setTyping] = useState(null);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Initialize Socket.IO
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const socketUrl = backendUrl.replace('/api', '').replace('https://', 'wss://').replace('http://', 'ws://');
    
    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      path: '/socket.io'
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
      if (user?.id) {
        newSocket.emit('authenticate', { user_id: user.id });
      }
    });

    newSocket.on('new_message', (message) => {
      if (selectedConversation && message.conversation_id === selectedConversation.thread_id) {
        setMessages(prev => [...prev, message]);
      }
      // Refresh conversations list
      fetchConversations();
    });

    newSocket.on('user_typing', (data) => {
      if (selectedConversation && data.conversation_id === selectedConversation.thread_id) {
        setTyping(data.user_name);
      }
    });

    newSocket.on('user_stop_typing', () => {
      setTyping(null);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [user?.id]);

  // Join conversation room when selected
  useEffect(() => {
    if (socket && selectedConversation) {
      socket.emit('join_conversation', { conversation_id: selectedConversation.thread_id });
      
      return () => {
        socket.emit('leave_conversation', { conversation_id: selectedConversation.thread_id });
      };
    }
  }, [socket, selectedConversation]);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.thread_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const response = await api.get(`/messages/thread/${threadId}`);
      setMessages(response.data);
    } catch (error) {
      setMessages([]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      // Send via API (which also broadcasts via Socket.IO)
      const response = await api.post('/messages', {
        conversation_id: selectedConversation.thread_id,
        recipient_id: selectedConversation.recipient_id,
        content: newMessage
      });
      
      setNewMessage('');
      
      // Fetch messages to ensure sync
      fetchMessages(selectedConversation.thread_id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleTyping = () => {
    if (socket && selectedConversation) {
      socket.emit('typing', {
        conversation_id: selectedConversation.thread_id,
        user_id: user.id,
        user_name: user.full_name
      });

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('stop_typing', {
          conversation_id: selectedConversation.thread_id,
          user_id: user.id
        });
      }, 2000);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const searchUsers = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchingUsers(true);
    try {
      const response = await api.get(`/users/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers(userSearch);
    }, 300);
    return () => clearTimeout(delayedSearch);
  }, [userSearch, searchUsers]);

  const startConversation = async (recipientUser) => {
    try {
      const response = await api.post(`/messages/start-conversation?recipient_user_id=${recipientUser.id}`);
      const conversationId = response.data.data.conversation_id;
      
      // Set selected conversation
      setSelectedConversation({
        thread_id: conversationId,
        participant_name: recipientUser.full_name,
        company_name: recipientUser.company_name || '',
        recipient_id: recipientUser.id,
        last_message: '',
        unread_count: 0
      });
      
      setShowNewConversation(false);
      setUserSearch('');
      setSearchResults([]);
      
      // Refresh conversations
      fetchConversations();
      
      toast.success(`Started conversation with ${recipientUser.full_name}`);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-outfit font-bold text-ocean-900">Messages</h1>
              <p className="text-slate-600 mt-2">Communicate with network members</p>
            </div>
            <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
              <DialogTrigger asChild>
                <Button className="bg-cyan-500 hover:bg-cyan-600" data-testid="new-conversation-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Message
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Start New Conversation</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  
                  {searchingUsers ? (
                    <div className="text-center py-4 text-slate-500">Searching...</div>
                  ) : searchResults.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {searchResults.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => startConversation(u)}
                          className="p-3 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                          data-testid={`user-result-${u.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-cyan-600" />
                            </div>
                            <div>
                              <p className="font-medium">{u.full_name}</p>
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                {u.company_name && (
                                  <>
                                    <Building2 className="h-3 w-3" />
                                    {u.company_name}
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : userSearch ? (
                    <div className="text-center py-4 text-slate-500">No users found</div>
                  ) : (
                    <div className="text-center py-4 text-slate-500">
                      Search for a member to start a conversation
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-3 gap-6 h-[calc(100vh-250px)]">
            {/* Conversations List */}
            <Card className="md:col-span-1 flex flex-col">
              <CardHeader className="pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="conversation-search"
                  />
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto">
                {filteredConversations.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="mb-2">No conversations yet</p>
                    <p className="text-sm">Click "New Message" to start a conversation</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredConversations.map((conv) => (
                      <div
                        key={conv.thread_id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedConversation?.thread_id === conv.thread_id
                            ? 'bg-cyan-50 border-l-4 border-cyan-500'
                            : 'hover:bg-slate-50'
                        }`}
                        data-testid={`conversation-${conv.thread_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-slate-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{conv.participant_name}</p>
                            <p className="text-xs text-slate-500 truncate">{conv.last_message || 'No messages yet'}</p>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-cyan-500">{conv.unread_count}</Badge>
                          )}
                        </div>
                        {conv.last_message_at && (
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(conv.last_message_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Messages Area */}
            <Card className="md:col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  <CardHeader className="border-b">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="md:hidden"
                        onClick={() => setSelectedConversation(null)}
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{selectedConversation.participant_name}</CardTitle>
                        <p className="text-sm text-slate-500">{selectedConversation.company_name}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-y-auto p-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <p>No messages in this conversation yet</p>
                        <p className="text-sm mt-1">Start the conversation below</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg p-3 ${
                                msg.sender_id === user?.id
                                  ? 'bg-cyan-500 text-white'
                                  : 'bg-slate-100 text-slate-900'
                              }`}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${
                                msg.sender_id === user?.id ? 'text-cyan-100' : 'text-slate-400'
                              }`}>
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        ))}
                        {typing && (
                          <div className="flex justify-start">
                            <div className="bg-slate-100 rounded-lg p-3 text-sm text-slate-500">
                              {typing} is typing...
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </CardContent>
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          handleTyping();
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        data-testid="message-input"
                      />
                      <Button
                        onClick={sendMessage}
                        className="bg-cyan-500 hover:bg-cyan-600"
                        disabled={!newMessage.trim()}
                        data-testid="send-message-btn"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <CardContent className="flex-1 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium mb-2">Select a conversation</p>
                    <p className="text-sm">Choose a conversation from the list or start a new one</p>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Messages;
